import { Divider, Icon, Stack, Typography } from '@mui/material';
import { BellIcon, TypographyWithOverflowHint } from '@schooly/style';
import { FC, Fragment, ReactNode, useCallback, useRef } from 'react';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { DropdownSelectRow } from '../DropdownSelectRow';

export type AttendanceCodeSelectOption = {
  label: string;
  id: string;
  avatar: ReactNode;
  withNotification?: boolean;
};

type AttendanceCodeSelectProps = {
  options: AttendanceCodeSelectOption[][];
  selectedOption?: AttendanceCodeSelectOption;
  onSelect: (option: AttendanceCodeSelectOption) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent' | 'hasValues'>;

export const AttendanceCodeSelect: FC<AttendanceCodeSelectProps> = ({
  options,
  selectedOption,
  onSelect,
  disabled,
  ...dropdownProps
}) => {
  const dropdown = useRef<DropdownSelect | null>(null);

  const handleSelect = useCallback(
    (option: AttendanceCodeSelectOption) => {
      if (option.id === selectedOption?.id) return;

      onSelect(option);
      dropdown.current?.close();
    },
    [onSelect, selectedOption?.id],
  );

  const renderOption = useCallback(
    (option: AttendanceCodeSelectOption) => (
      <Stack direction="row" gap={1} alignItems="center">
        {option.avatar}
        <TypographyWithOverflowHint variant="h3" color="inherit">
          {option.label}
        </TypographyWithOverflowHint>
        {!!option.withNotification && (
          <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5) })}>
            <BellIcon />
          </Icon>
        )}
      </Stack>
    ),
    [],
  );

  const renderContent = useCallback(
    () => (
      <Stack gap={0.25} p={1}>
        {options
          .filter(
            (section): section is [AttendanceCodeSelectOption, ...AttendanceCodeSelectOption[]] =>
              section.length > 0,
          )
          .map((section, index, array) => (
            <Fragment key={section[0].id}>
              {section.map((option) => (
                <DropdownSelectRow
                  key={option.id}
                  isSelected={selectedOption?.id === option.id}
                  onClick={() => handleSelect(option)}
                >
                  {renderOption(option)}
                </DropdownSelectRow>
              ))}
              {index !== array.length - 1 && <Divider />}
            </Fragment>
          ))}
      </Stack>
    ),
    [handleSelect, options, renderOption, selectedOption?.id],
  );

  return (
    <DropdownSelect
      {...dropdownProps}
      ref={dropdown}
      disabled={disabled}
      hasValues={!!selectedOption}
      renderContent={renderContent}
    >
      {() => {
        if (!selectedOption) return null;

        return (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ maxWidth: '100%', pointerEvents: 'none' }}
          >
            {selectedOption.avatar}
            <Typography variant="h3" color="inherit" noWrap>
              {selectedOption.label}
            </Typography>
            {!!selectedOption.withNotification && (
              <Icon inverse sx={(theme) => ({ fontSize: theme.spacing(1.5) })}>
                <BellIcon />
              </Icon>
            )}
          </Stack>
        );
      }}
    </DropdownSelect>
  );
};
